<template>
  <div class="account-page bg-slate-50 min-h-screen">
    
    <div class="container mx-auto py-8 px-4 md:px-6">
      <!-- Current Subscription Card -->
      <div class="card mb-8">
        <div class="card-header">
          <h2 class="text-xl font-semibold text-gray-800">Current Subscription</h2>
        </div>
        <div class="card-body">
          <div class="bg-white p-5 rounded-lg shadow-sm flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <div class="flex items-center">
                <div class="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mr-4">
                  <v-icon class="text-primary">mdi-star-circle</v-icon>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-gray-900">
                    {{ planName }} Plan
                    <span class="text-sm font-medium text-gray-600">
                      {{ authUser.signup_offer_subscribed ? "(Signup Offer)" : authUser.billing_period ? `(${authUser.billing_period})` : "" }}
                    </span>
                  </h3>
                  <p class="text-sm text-gray-600 mt-1">You can upgrade or modify your account subscription at any time.</p>
                </div>
              </div>
            </div>
            <div class="mt-4 md:mt-0">
              <router-link to="/subscription">
                <v-btn color="primary" rounded>
                  <v-icon left>mdi-pencil</v-icon>
                  Change Plan
                </v-btn>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Credits Usage Card -->
      <div class="card mb-8">
        <div class="card-header">
          <h2 class="text-xl font-semibold text-gray-800">Plan Usage</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Posts Credits -->
            <div class="bg-white p-5 rounded-lg shadow-sm">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3">
                  <v-icon color="blue" small>mdi-post</v-icon>
                </div>
                <h3 class="font-semibold text-gray-800">Posts</h3>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm text-gray-600">
                  {{ this.fly_bl - this.authUser.fly_credits < 0 ? 0 : this.fly_bl - this.authUser.fly_credits }} used
                </span>
                <span class="text-sm text-gray-800 font-medium">{{ this.fly_bl }} total</span>
              </div>
              <div class="progress-container">
                <div class="progress-bar" :style="{ width: consumedCreditsProgress }"></div>
              </div>
            </div>
            
            <!-- Text AI Writer -->
            <div class="bg-white p-5 rounded-lg shadow-sm">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mr-3">
                  <v-icon color="green" small>mdi-robot</v-icon>
                </div>
                <h3 class="font-semibold text-gray-800">Text AI Writer Actions</h3>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm text-gray-600">
                  {{ this.txt_cr - this.authUser.fly_ai_text_credits < 0 ? 0 : this.txt_cr - this.authUser.fly_ai_text_credits }} used
                </span>
                <span class="text-sm text-gray-800 font-medium">{{ this.txt_cr }} total</span>
              </div>
              <div class="progress-container">
                <div class="progress-bar" :style="{ width: textCreditsProgress }"></div>
              </div>
            </div>
            
            <!-- Storage Usage -->
            <div class="bg-white p-5 rounded-lg shadow-sm">
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mr-3">
                  <v-icon color="purple" small>mdi-database</v-icon>
                </div>
                <h3 class="font-semibold text-gray-800">Storage</h3>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm text-gray-600">{{ this.authUser.fly_storage }} GB used</span>
                <span class="text-sm text-gray-800 font-medium">{{ this.maxStorage }} GB total</span>
              </div>
              <div class="progress-container">
                <div class="progress-bar" :style="{ width: storageProgress }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Add-on Credits Card -->
      <div v-if="user_addon_credits && user_addon_credits.length > 0" class="card mb-8">
        <div class="card-header flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">Add-on Credits</h2>
          <router-link to="/subscription">
            <v-btn color="primary" small rounded>
              Purchase Add-ons
            </v-btn>
          </router-link>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div 
              v-for="(addon, i) in user_addon_credits" 
              :key="i"
              class="bg-white p-5 rounded-lg shadow-sm"
            >
              <div class="flex items-center mb-3">
                <div class="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mr-3">
                  <v-icon color="amber" small>mdi-gift</v-icon>
                </div>
                <h3 class="font-semibold text-gray-800">{{ addon.name }}</h3>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm text-gray-600">{{ addon.used }} used</span>
                <span class="text-sm text-gray-800 font-medium">{{ addon.total }} total</span>
              </div>
              <div class="progress-container">
                <div class="progress-bar" :style="{
                  width: consumedAddonCreditsProgress(addon.remaining, addon.total)
                }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Billing History Card -->
      <div v-if="subscriptionsHistory?.length > 0" class="card mb-8">
        <div class="card-header">
          <h2 class="text-xl font-semibold text-gray-800">Billing History</h2>
        </div>
        <div class="card-body">
          <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Period</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchased on</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cancelled on</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(subscription, index) in subscriptionsHistory" :key="index">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-4 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full" 
                      :class="{
                        'bg-green-100 text-green-800': subscription.status === 'active',
                        'bg-red-100 text-red-800': subscription.status === 'cancelled',
                        'bg-blue-100 text-blue-800': !isSubscriptionActive && index == 0 && subscription.status !== 'active'
                      }"
                    >
                      <span v-if="!isSubscriptionActive && index == 0 && subscription.status !== 'active'">
                        In progress
                        <v-progress-circular
                          indeterminate
                          :size="16"
                          :width="2"
                          class="ml-2"
                          color="primary"
                        ></v-progress-circular>
                      </span>
                      <span v-else>{{ subscription.status }}</span>
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {{ isINR ? "INR" : "USD" }} {{ subscription.amount }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {{ subscription.billing_period ? subscription.billing_period : "" }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {{ subscription.created_at.seconds
                      ? formatDate(subscription.created_at.seconds * 1000)
                      : formatDate(subscription.created_at) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {{ subscription.status != "active"
                      ? subscription.cancelled_at?.seconds
                        ? formatDate(subscription.cancelled_at.seconds * 1000)
                        : formatDate(subscription.cancelled_at)
                      : "--" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <!-- Payment Information Card -->
      <div v-if="!isINR && subscriptionsHistory?.length > 0" class="card">
        <div class="card-header">
          <h2 class="text-xl font-semibold text-gray-800">Payment Information</h2>
        </div>
        <div class="card-body">
          <div class="bg-white p-5 rounded-lg shadow-sm">
            <payment-method-details />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import "firebase/firestore";
import {
  doc,
  updateDoc,
  collection,
  getDoc,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  auth,
  colGallery,
  colUsers,
  db,
  functions,
  flyplans,
  flyofferplans,
  subscriptions,
} from "@/utils/firebase.utils.js";
import PaymentMethodDetails from "@/components/account/PaymentMethodDetails.vue";
import PageHeader from "@/components/PageHeader.vue";
import moment from "moment";

export default {
  components: { 
    PaymentMethodDetails,
    PageHeader
  },
  data() {
    return {
      fly_bl: 0,
      plan_price_usd: 0,
      img_cr: 0,
      txt_cr: 0,
      maxCredits: 0,
      maxImageCredits: 0,
      maxTextCredits: 0,
      maxStorage: 0,
      youtubeapikey: "",
      showModal: false,
      currentSubscription: {},
      plan_price: 0,
      subscriptionsHistory: [],
      isSubscriptionActive: true,
      pollCount: 0,
      maxPollAttempts: 30,
      creditTypes: [
        {
          key: "ai_writer_actions",
          name: "Text AI Writer Actions",
        },
        {
          key: "ai_image_actions",
          name: "AI Image Actions",
        },
        {
          key: "posts",
          name: "Posts",
        },
        {
          key: "brands",
          name: "Brands",
        },
        {
          key: "storage",
          name: "Storage",
        },
        {
          key: "ai_generated_posts",
          name: "AI Generated Posts",
        },
        {
          key: "ai_image_gen",
          name: "AI Generated Images",
        },
      ],
      user_addon_credits: [],
    };
  },
  computed: {
    // Other computed properties...
    isINR() {
      return (
        this.authUser &&
        this.authUser.country_code &&
        this.authUser.country_code === "IN"
      );
    },
    planName() {
      let plan = this.authUser.fly_plan;
      if (
        plan == "lite" ||
        plan == "lite_3m" ||
        plan == "lite_6m" ||
        plan == "lite_12m"
      ) {
        return "Personal";
      } else if (
        plan == "boost" ||
        plan == "boost_3m" ||
        plan == "boost_6m" ||
        plan == "boost_12m"
      ) {
        return "Enterprise";
      } else if (
        plan == "plus" ||
        plan == "plus_3m" ||
        plan == "plus_6m" ||
        plan == "plus_12m"
      ) {
        return "Professional";
      }
      return "Free";
    },
    consumedCreditsProgress() {
      if((this.maxCredits - this.authUser.fly_credits)<0){
        return "0%"
      }
      const progressPercentage =
        ((this.maxCredits - this.authUser.fly_credits) / this.maxCredits) * 100;
      return progressPercentage + "%";
    },
    imageCreditsProgress() {
      const progressPercentage =
        ((this.maxImageCredits - this.authUser.fly_ai_image_credits) /
          this.maxImageCredits) *
        100;
      return progressPercentage + "%";
    },
    textCreditsProgress() {
      if((this.maxTextCredits - this.authUser.fly_ai_text_credits)<0){
        return "0%"
      }
      const progressPercentage =
        ((this.maxTextCredits - this.authUser.fly_ai_text_credits) /
          this.maxTextCredits) *
        100;
      return progressPercentage + "%";
    },
    storageProgress() {
      const progressPercentage =
        (this.authUser.fly_storage / this.maxStorage) * 100;
      return progressPercentage + "%";
    },
    name() {
      if (this.user?.firstName) {
        return `${this.user.firstName} ${this.user.lastName}`;
      }
      return `${this.authUser.username}`;
    },
  },
  methods: {
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    consumedAddonCreditsProgress(rem, total) {
      const progressPercentage = ((total - rem) / total) * 100;
      return progressPercentage + "%";
    },
    async fetchSubscriptionsHistory() {
      try {
        let q = query(
          collection(doc(subscriptions, this.uid), "history"),
          orderBy("created_at", "desc")
        );

        this.subscriptionsHistory = (await getDocs(q)).docs.map((i) => ({
          ...i.data(),
          id: i.id,
        }));
      } catch (e) {
        console.error("Error fetching fly plan history:", e);
      }
    },
    async fetchData() {
      try {
        let flyPlanDoc = await getDoc(doc(flyplans, this.authUser.fly_plan));
        if (this.authUser.offer_availed) {
          flyPlanDoc = await getDoc(doc(flyofferplans, this.authUser.fly_plan));
        }
        if (flyPlanDoc.exists()) {
          this.maxCredits = flyPlanDoc.data().limits.posts;
          this.fly_bl = this.maxCredits;
          this.plan_price = flyPlanDoc.data().monthly_price_inr;
          this.plan_price_usd = flyPlanDoc.data().monthly_price_usd;

          this.maxImageCredits = flyPlanDoc.data().limits.ai_image_actions;
          this.img_cr = this.maxImageCredits;

          this.maxTextCredits = flyPlanDoc.data().limits.ai_text_actions;
          this.txt_cr = this.maxTextCredits;

          this.maxStorage = flyPlanDoc.data().limits.storage;
        }
      } catch (error) {
        console.error("Error fetching fly plan data:", error);
      }
    },

    async fetchTotalCredits() {
      // Ensure the function is resilient to the absence of either credit type
      if (
        !this.authUser.addon_credits &&
        !this.authUser.addon_monthly_credits
      ) {
        console.log("No addon credit data available.");
        return; // Early exit if both credit types are absent
      }
      const currentDate = new Date();

      this.creditTypes.forEach((type) => {
        // Initialize variables to store credit data
        let totalOneTimeCredits = 0,
          usedOneTimeCredits = 0,
          remainingOneTimeCredits = 0,
          totalMonthlyCredits = 0,
          usedMonthlyCredits = 0,
          remainingMonthlyCredits = 0;

        // Process one-time credits if available
        if (
          this.authUser.addon_credits &&
          this.authUser.addon_credits.hasOwnProperty(type.key)
        ) {
          const totalKey = `total_${type.key}`;
          totalOneTimeCredits = this.authUser.addon_credits[totalKey] || 0;
          usedOneTimeCredits =
            totalOneTimeCredits - this.authUser.addon_credits[type.key];
          remainingOneTimeCredits = this.authUser.addon_credits[type.key];
        }

        // Process monthly credits if available
        if (
          this.authUser.addon_monthly_credits &&
          this.authUser.addon_monthly_credits.hasOwnProperty(type.key)
        ) {
          const totalKey = `total_${type.key}`;
          const validityKey = `${type.key}_valid_until`;
          let validUntil = this.authUser.addon_monthly_credits[validityKey];
          let isCancelled = this.authUser.addon_monthly_credits[validityKey];
          if (validUntil?.seconds) {
            validUntil = new Date(validUntil.seconds * 1000);
          } else validUntil = new Date(validUntil);

          // addon cancelled but not expired
          if (
            !isCancelled ||
            isCancelled == undefined ||
            (validUntil && validUntil.getTime() > currentDate.getTime())
          ) {
            totalMonthlyCredits =
              this.authUser.addon_monthly_credits[totalKey] || 0;
            usedMonthlyCredits =
              totalMonthlyCredits -
              this.authUser.addon_monthly_credits[type.key];
            remainingMonthlyCredits =
              this.authUser.addon_monthly_credits[type.key];
          }
          // addon expired
          if (validUntil && validUntil.getTime() < currentDate.getTime()) {
            totalMonthlyCredits = 0;
            usedMonthlyCredits = 0;
            remainingMonthlyCredits = 0;
          }
        }

        // Calculate total and used credits across both types
        let total_credits = totalOneTimeCredits + totalMonthlyCredits;
        let used_credits = usedOneTimeCredits + usedMonthlyCredits;
        let remaining_credits =
          remainingOneTimeCredits + remainingMonthlyCredits;

        if (totalOneTimeCredits > 0 || totalMonthlyCredits > 0) {
          this.user_addon_credits.push({
            name: type.name,
            total: total_credits,
            used: used_credits,
            remaining: remaining_credits,
          });
        }
      });
    },

    async getCurrentSubscription() {
      let subscriptionRef = doc(collection(db, "fly_subscriptions"), this.uid);
      this.currentSubscription = (await getDoc(subscriptionRef)).data() || {};
      if (this.currentSubscription.status === "active") {
        if (!this.isSubscriptionActive) {
          this.$router.go();
        }
      } else {
        this.isSubscriptionActive = false;
        // Increment the poll count
        this.pollCount++;
        // If status is not 'active' and max attempts not reached, keep polling
        setTimeout(this.getCurrentSubscription, 5000); // Poll every 5 seconds
      }
    },
    buyCredits() {
      this.$router.push("/subscription");
    },
    async generateApiKey() {
      // Generate a new API key
      const newApiKey = uuidv4();
      try {
        // Get the current user's ID
        const userId = auth.currentUser.uid;

        // Update the `fly_api_key` field in the user's document
        let data = {
          fly_api_key: newApiKey,
          fly_api_key_generated_at: new Date().toLocaleString(),
        };
        await updateDoc(doc(colUsers, userId), data);
        console.log("API key stored in Firestore.");
      } catch (error) {
        console.error("Error storing API key in Firestore:", error);
      }
    },
    regenerateApiKey() {
      const confirmRegenerate = window.confirm(
        "Are you sure you want to regenerate the API Key? This will invalidate the current API Key."
      );

      if (!confirmRegenerate) {
        return; // Cancel regeneration
      }

      // Regenerate the API key
      this.generateApiKey();
    },
    async updateYoutubeApiKey() {
      if (this.youtubeapikey) {
        try {
          // Get the current user's ID
          const userId = auth.currentUser.uid;

          // Update the `fly_api_key` field in the user's document
          let data = {
            yt_data_api_key: this.youtubeapikey,
          };
          await updateDoc(doc(colUsers, userId), data);

          console.log("API key stored in Firestore.");
        } catch (error) {
          console.error("Error storing API key in Firestore:", error);
        }
      } else {
        alert("Please enter a valid YouTube Data API Key.");
      }
    },

    closeModal() {
      this.showModal = false;
    },
    async cancelSubscription() {
      let subscriptionRef = doc(collection(db, "fly_subscriptions"), this.uid);
      let currentSubscription = (await getDoc(subscriptionRef)).data() || {};
      console.log("reesult", currentSubscription);
      let data = { subscription: currentSubscription.subscription_id };
      let subscribe = httpsCallable(functions, "razorpaySubscribe");
      let result = (await subscribe(data)).data;
      console.log("res", result);
    },
    copyLinkToClipboard(link) {
      const el = document.createElement("textarea");
      el.value = link;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      alert("Link copied to clipboard!");
    },
  },
  mounted() {
    this.fetchTotalCredits();
    this.fetchData();
    this.fetchSubscriptionsHistory();
    this.getCurrentSubscription();
  },
};
</script>

<style scoped>
.account-page {
  min-height: 100vh;
}

.container {
  max-width: 1280px;
}

.card {
  background-color: transparent;
  border-radius: 0.75rem;
  overflow: hidden;
}

.card-header {
  padding: 1.25rem 1.5rem;
  background-color: transparent;
}

.card-body {
  padding: 0;
}

.progress-container {
  height: 10px;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #3C50E0;
  border-radius: 9999px;
  transition: width 0.3s ease;
}

table {
  border-collapse: separate;
  border-spacing: 0;
}

th {
  font-weight: 600;
  padding: 0.75rem 1rem;
  text-align: left;
}

td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  th, td {
    padding: 0.5rem;
  }
}
</style>
